import { useState } from 'react'
import { router, useForm } from '@inertiajs/react'
import type { FormEvent } from 'react'
import Layout, { Card } from '@/components/Layout'
import { Sheet, SheetPad } from '@/components/Sheet'
import { Button } from '@/components/ui'
import { Field, SubmitButton } from '@/components/AuthShell'
import LocaleSwitcher from '@/components/LocaleSwitcher'
import { t } from '@/lib/i18n'
import type { SubscriptionProps } from '@/types'

const HOURS = Array.from({ length: 24 }, (_, i) => i)

// 每日提醒。默认关闭——把人静默加入每日邮件是骚扰，也和隐私政策里「只发事务邮件」冲突。
function ReminderSettings({ reminderHour }: { reminderHour: number | null }) {
  return (
    <Card title={`🔔 ${t('account.reminder.title')}`}>
      <div className="grid gap-2.5 max-w-[420px]">
        <div className="text-[14px] text-muted leading-relaxed">
          {t('account.reminder.hint')}
        </div>
        <select
          value={reminderHour ?? ''}
          onChange={(e) => router.patch('/account', { reminder_hour: e.target.value }, { preserveScroll: true })}
          className="justify-self-start border border-line rounded-[10px] px-2.5 py-2 outline-none focus:border-brand"
        >
          <option value="">{t('account.reminder.off')}</option>
          {HOURS.map((h) => <option key={h} value={h}>{t('account.reminder.daily_at', { time: `${String(h).padStart(2, '0')}:00` })}</option>)}
        </select>
        <div className="text-[13px] text-muted">
          {t('account.reminder.unsubscribe_note')}
        </div>
      </div>
    </Card>
  )
}

// 订阅状态与「管理订阅」入口。
//
// 取消 / 改支付方式 / 看发票全部在 Paddle 的 customer portal 里做，我们不自建：
// Paddle 是 merchant of record，发票与税务是它的职责，重做一遍只会两处不一致。
function Billing({ entitlement, subscription }: {
  entitlement: 'free' | 'family'
  subscription: SubscriptionProps | null
}) {
  return (
    <Card title={`💳 ${t('account.billing.title')}`}>
      <div className="grid gap-3 max-w-[520px]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[15px] font-semibold">
            {entitlement === 'family' ? t('account.billing.plan_family') : t('account.billing.plan_free')}
          </span>
          {subscription && (
            <span className="text-[12px] font-bold text-muted bg-bg px-2 py-1 rounded-full">
              {subscription.status}
            </span>
          )}
          {/* 沙盒订阅要当场看得见，否则你会把测试单当成真实收入。 */}
          {subscription?.environment === 'sandbox' && (
            <span className="text-[12px] font-bold text-amber-strong bg-amber-tint px-2 py-1 rounded-full">
              🧪 {t('account.billing.sandbox')}
            </span>
          )}
        </div>

        {subscription?.manageable ? (
          <button
            type="button"
            onClick={() => router.post('/account/billing_portal')}
            className="justify-self-start bg-card border border-line font-semibold text-sm px-4 py-2 rounded-[10px] hover:border-brand"
          >
            {t('account.billing.manage')}
          </button>
        ) : (
          <a
            href="/pricing"
            className="justify-self-start text-white grad-bg font-bold text-sm px-4 py-2 rounded-[10px] hover:opacity-90"
          >
            {t('account.billing.see_pricing')}
          </a>
        )}

        <div className="text-[13px] text-muted">
          {t('account.billing.refund_note')}
          <a href="/refund" className="text-brand ml-1">{t('account.billing.refund_link')}</a>
        </div>
      </div>
    </Card>
  )
}

export default function Edit({ email_address, display_name, reminder_hour, entitlement, subscription }: {
  email_address: string
  display_name: string | null
  reminder_hour: number | null
  entitlement: 'free' | 'family'
  subscription: SubscriptionProps | null
}) {
  const pwForm = useForm({ password_challenge: '', password: '', password_confirmation: '' })
  const delForm = useForm({ password_challenge: '' })
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const savePassword = (e: FormEvent) => {
    e.preventDefault()
    pwForm.patch('/account', {
      preserveScroll: true,
      onSuccess: () => pwForm.reset(),
    })
  }

  // 三道闸：先展开这一段，再输当前密码，最后过一个确认 sheet。
  // 删除不可恢复，不能一键触发。最后那道原来是 window.confirm ——
  // 换成 sheet 的理由见规矩 §5：居中系统弹窗在手机上要伸手到屏幕中间、样式不受控。
  const [confirmSheet, setConfirmSheet] = useState(false)

  const deleteAccount = (e: FormEvent) => {
    e.preventDefault()
    setConfirmSheet(true)
  }

  const reallyDelete = () => {
    setConfirmSheet(false)
    delForm.delete('/account')
  }

  return (
    <Layout>
      <Card title={`⚙️ ${t('account.title')}`}>
        <div className="grid gap-1.5 max-w-[360px]">
          <span className="text-sm font-semibold text-ink">{t('account.email')}</span>
          <div className="border border-line rounded-[10px] px-3 py-2.5 bg-bg text-muted">{email_address}</div>
          <span className="text-[13px] text-muted">{t('account.email_note')}</span>
        </div>

        {/* 显示名。hint 必须写清楚**谁会看到**——否则会被当成昵称随手填，
            而它的用途是替掉此前直接展示给共享者的登录邮箱。 */}
        <div className="grid gap-1.5 max-w-[360px] mt-4">
          <Field
            label={t('account.display_name.label')}
            defaultValue={display_name ?? ''}
            placeholder={t('account.display_name.placeholder')}
            onBlur={(e) => router.patch('/account', { display_name: e.target.value }, { preserveScroll: true })}
          />
          <span className="text-[13px] text-muted">{t('account.display_name.hint')}</span>
        </div>
      </Card>

      {/* 只有这里给「自动」：它是写进 users.locale 的偏好，也是发邮件时用的那一份。
          右上角那个快速切换只给中文 / English。 */}
      <Card title={`🌐 ${t('account.language')}`}>
        {/* data-locale-setting 是稳定的测试锚点：卡片标题会随语言变，
            系统测试不该为了找它先猜当前是哪种语言。 */}
        <div data-locale-setting>
          <LocaleSwitcher withAuto />
        </div>
      </Card>

      {/* 导出。放在提醒设置之前、语言之后：它属于「这个账号本身」那一组，
          而不是危险操作 —— 所以刻意**不**放进底部那张红色的删除卡里。
          是个普通 <a>，不是 router：Inertia 的 visit 处理不了文件下载。 */}
      <Card title={`📦 ${t('account.export.title')}`}>
        <div className="grid gap-2.5">
          <span className="text-[13px] text-muted">{t('account.export.body')}</span>
          <a
            href="/account/export"
            data-account-export
            className="inline-flex items-center justify-center min-h-tap px-4 rounded-chip bg-sunk text-ink text-sm font-semibold">
            {t('account.export.download')}
          </a>
          <span className="text-[13px] text-faint">{t('account.export.note')}</span>
        </div>
      </Card>

      <ReminderSettings reminderHour={reminder_hour} />

      <Billing entitlement={entitlement} subscription={subscription} />

      <Card title={`🔒 ${t('account.password.title')}`}>
        <form onSubmit={savePassword} className="grid gap-4 max-w-[360px]">
          <Field
            label={t('account.password.current')}
            type="password"
            autoComplete="current-password"
            required
            value={pwForm.data.password_challenge}
            error={pwForm.errors.password_challenge}
            onChange={(e) => pwForm.setData('password_challenge', e.target.value)}
          />
          <Field
            label={t('auth.reset_password.new_password')}
            type="password"
            autoComplete="new-password"
            required
            maxLength={72}
            placeholder={t('auth.min_length')}
            value={pwForm.data.password}
            error={pwForm.errors.password}
            onChange={(e) => pwForm.setData('password', e.target.value)}
          />
          <Field
            label={t('auth.reset_password.confirm_new')}
            type="password"
            autoComplete="new-password"
            required
            maxLength={72}
            value={pwForm.data.password_confirmation}
            error={pwForm.errors.password_confirmation}
            onChange={(e) => pwForm.setData('password_confirmation', e.target.value)}
          />
          <SubmitButton disabled={pwForm.processing}>{pwForm.processing ? t('account.password.submitting') : t('account.password.submit')}</SubmitButton>
        </form>
      </Card>

      <Card title={`🗑 ${t('account.delete.title')}`}>
        <div className="max-w-[520px] grid gap-3">
          <div className="text-[14px] leading-relaxed text-muted">
            {t('account.delete.intro')}
            <ul className="list-disc pl-5 mt-1.5 grid gap-1">
              <li>{t('account.delete.item_own')}</li>
              <li>{t('account.delete.item_shared')}</li>
              <li>{t('account.delete.item_access')}</li>
            </ul>
          </div>

          {!confirmingDelete ? (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              className="justify-self-start border border-red-line text-red-strong font-semibold text-sm px-4 py-2 rounded-[10px] hover:bg-red-tint"
            >
              {t('account.delete.start')}
            </button>
          ) : (
            <form onSubmit={deleteAccount} className="grid gap-3 border border-red-line bg-red-tint rounded-xl p-3.5">
              <Field
                label={t('account.delete.confirm_label')}
                type="password"
                autoComplete="current-password"
                required
                value={delForm.data.password_challenge}
                error={delForm.errors.password_challenge}
                onChange={(e) => delForm.setData('password_challenge', e.target.value)}
              />
              <div className="flex gap-2.5 flex-wrap">
                <button
                  type="submit"
                  disabled={delForm.processing}
                  className="bg-red text-white font-bold text-sm px-4 py-2 rounded-[10px] disabled:opacity-60"
                >
                  {delForm.processing ? t('account.delete.deleting') : t('account.delete.confirm')}
                </button>
                <button
                  type="button"
                  onClick={() => { setConfirmingDelete(false); delForm.reset() }}
                  className="bg-card border border-line font-semibold text-sm px-4 py-2 rounded-[10px]"
                >
                  {t('goals.show.cancel')}
                </button>
              </div>
            </form>
          )}

          <div className="text-[13px] text-muted">
            {t('account.delete.export_note')}
            <a href="/privacy" className="text-brand ml-1">{t('account.delete.privacy_link')}</a>
          </div>
        </div>
      </Card>

      <Sheet
        open={confirmSheet}
        onClose={() => setConfirmSheet(false)}
        label={t('account.delete.title')}
        title={t('account.delete.title')}
      >
        <div className="mx-5 mb-3.5 px-3.5 py-3 rounded-ctl bg-red-tint text-red-strong text-[13px] leading-[1.45]">
          {t('account.delete.final_confirm')}
        </div>
        <SheetPad>
          <div className="grid gap-2">
            <Button variant="danger" onClick={reallyDelete}>{t('account.delete.confirm')}</Button>
            <Button variant="plain" onClick={() => setConfirmSheet(false)}>{t('people.keep')}</Button>
          </div>
        </SheetPad>
      </Sheet>
    </Layout>
  )
}
